import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  InventoryFeature,
  LabAutomationFeature,
  ProcessesFeature,
  RegistryFeature,
  ResultsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {SidepanelExpansionState} from 'constants/NavConstants';
import {areMonomersEnabledForUser} from 'molbio/monomers/MonomersUtil';
import {RootCrumb} from 'nav/Breadcrumbs';
import SampleTypesSettings from 'sample-types/SampleTypesSettings';
import DeveloperConsoleSettings, {shouldShowDeveloperConsole} from 'sidebar/DeveloperConsoleSettings';
import InventorySettings from 'sidebar/InventorySettings';
import MonomerSettings from 'sidebar/MonomerSettings';
import NotebookSettings from 'sidebar/NotebookSettings';
import ParameterLibrarySettings from 'sidebar/ParameterLibrarySettings';
import PolicySettings from 'sidebar/PolicySettings';
import ProcessSchemaSettings from 'sidebar/ProcessSchemaSettings';
import RegistrySettings from 'sidebar/RegistrySettings';
import RequestSchemaSettings from 'sidebar/RequestSchemaSettings';
import ResultSchemaSettings from 'sidebar/ResultSchemaSettings';
import RunSchemaSettings from 'sidebar/RunSchemaSettings';
import SchemaInterfaceSettings from 'sidebar/SchemaInterfaceSettings';
import {SettingsSection} from 'sidebar/SidebarConstants';
import StudySchemaSettings from 'sidebar/StudySchemaSettings';
import TemplateCollectionsSettings, {TemplateCollectionType} from 'sidebar/TemplateCollectionsSettings';
import TestDefinitionSettings from 'sidebar/TestDefinitionSettings';
import ToolsSettings, {isToolsSectionEnabled} from 'sidebar/ToolsSettings';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as nyc from 'ui-library/NYC';
import UnitDictionarySettings from 'units/UnitDictionarySettings';

import ListingHeader from './header/ListingHeader';
import * as SidepanelActions from './SidepanelActions';
import SidepanelStore from './SidepanelStore';

const handleCollapseButtonClick = (): void => {
  const expansionState = SidepanelStore.getExpansionState();
  switch (expansionState) {
    case SidepanelExpansionState.COLLAPSED:
      SidepanelActions.uncollapse();
      break;
    case SidepanelExpansionState.SEMI_EXPANDED:
      SidepanelActions.collapse();
      break;
    default:
      throw new Error(`Unexpected SidepanelExpansionState ${expansionState}`);
  }
};
interface SidepanelCollapseButtonProps {
  expansionState: SidepanelExpansionState;
}

function SidepanelCollapseButton(props: SidepanelCollapseButtonProps): JSX.Element {
  return (
    <Button
      className={css(styles.collapseButton)}
      buttonStyle={ButtonStyle.TERTIARY_WHITE}
      icon={props.expansionState === SidepanelExpansionState.COLLAPSED ? 'chevron-right' : 'chevron-left'}
      size={ButtonSize.SMALL}
      onClick={handleCollapseButtonClick}
    />
  );
}

interface SettingsSidepanelHeaderProps {
  expansionState: SidepanelExpansionState;
}

function SettingsSidepanelHeader(props: SettingsSidepanelHeaderProps): JSX.Element {
  return (
    <ListingHeader
      buttons={
        <div className="u-mini-spacing-right">
          <SidepanelCollapseButton expansionState={props.expansionState} />
        </div>
      }
      crumbs={
        props.expansionState === SidepanelExpansionState.COLLAPSED ? undefined : <RootCrumb text="Settings" />
      }
      isExpanded={false}
    />
  );
}

interface SettingsSidepanelBodyProps {
  activeRegistryId: string | null;
  currentUser: EntityStoreUser;
  expansionState: SidepanelExpansionState;
  section: SettingsSection;
  template?: {
    templateCollection?: TemplateCollectionType;
  };
  templateCollection?: TemplateCollectionType;
}

function SettingsSidepanelBody(props: SettingsSidepanelBodyProps): JSX.Element {
  return (
    <nyc.Row fluid={true} className="u-position-relative">
      <nyc.Col sm={6} className="u-absolute-scroll-container u-overflow-x-hidden">
        <div
          className="u-spacing-top u-spacing-bottom"
          {...getTestProps({component: 'SettingsSidepanel', element: 'root'})}
        >
          {(isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) ||
            isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN})) && (
            <div className="u-spacing-top">
              <RegistrySettings activeRegistryId={props.activeRegistryId} section={props.section} />
            </div>
          )}
          {BenchlingConfig.SCHEMAS_ENABLE_INTERFACES && (
            <div className="u-spacing-top">
              <SchemaInterfaceSettings section={props.section} />
            </div>
          )}
          {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
            <div className="u-spacing-top">
              <InventorySettings section={props.section} />
            </div>
          )}
          <div className="u-spacing-top">
            <UnitDictionarySettings section={props.section} />
          </div>
          {BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY && (
            <div className="u-spacing-top">
              <ParameterLibrarySettings section={props.section} />
            </div>
          )}
          {areMonomersEnabledForUser() && (
            <div className="u-spacing-top">
              <MonomerSettings section={props.section} />
            </div>
          )}
          <div>
            <TemplateCollectionsSettings
              section={props.section}
              template={props.template}
              templateCollection={props.templateCollection}
            />
            <NotebookSettings section={props.section} />
          </div>
          {BenchlingConfig.STUDIES_ENABLE && (
            <div className="u-spacing-top">
              <StudySchemaSettings section={props.section} />
            </div>
          )}
          {BenchlingConfig.SAMPLE_TYPES_ENABLE && (
            <div className="u-spacing-top">
              <SampleTypesSettings section={props.section} />
            </div>
          )}
          {areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]) && (
            <div className="u-spacing-top">
              <RunSchemaSettings section={props.section} />
            </div>
          )}
          {isFeatureEnabled({feature: ResultsFeature.RESULTS_ADMIN}) && (
            <div className="u-spacing-top">
              <ResultSchemaSettings section={props.section} />
            </div>
          )}
          {BenchlingConfig.ENABLE_REQUESTS && (
            <div className="u-spacing-top">
              <RequestSchemaSettings section={props.section} />
            </div>
          )}
          {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_ADMIN}) && (
            <div className="u-spacing-top">
              <ProcessSchemaSettings section={props.section} />
            </div>
          )}
          {BenchlingConfig.SAMPLE_TESTS_ENABLE && (
            <div className="u-spacing-top">
              <TestDefinitionSettings section={props.section} />
            </div>
          )}
          {BenchlingConfig.ENABLE_POLICY_UI && (
            <div className="u-spacing-top" {...getTestProps({component: 'PolicySettings', element: 'root'})}>
              <PolicySettings section={props.section} />
            </div>
          )}
          {isToolsSectionEnabled() && (
            <div className="u-spacing-top">
              <ToolsSettings section={props.section} />
            </div>
          )}
          {shouldShowDeveloperConsole(props.currentUser.isTenantAdmin) && (
            <div className="u-spacing-top">
              <DeveloperConsoleSettings section={props.section} />
            </div>
          )}
        </div>
      </nyc.Col>
    </nyc.Row>
  );
}

export interface SettingsSidepanelProps {
  activeRegistryId: string | null;
  expansionState: SidepanelExpansionState;
  section: SettingsSection;
  template?: {
    templateCollection?: TemplateCollectionType;
  };
  templateCollection?: TemplateCollectionType;
}

export default function SettingsSidepanel(props: SettingsSidepanelProps): JSX.Element {
  return (
    <nyc.Container
      className={classNames('sidepanel', {
        [css(styles.collapsedSidepanel)]: props.expansionState === SidepanelExpansionState.COLLAPSED,
      })}
      size="sidepanel"
    >
      <nyc.Row>
        <SettingsSidepanelHeader expansionState={props.expansionState} />
      </nyc.Row>
      {props.expansionState === SidepanelExpansionState.SEMI_EXPANDED && (
        <SettingsSidepanelBody {...props} currentUser={EntityStore.currentUser()} />
      )}
    </nyc.Container>
  );
}

const styles = StyleSheet.create({
  collapsedSidepanel: {
    // Keep in sync with @width--sidepanel-collapsed in variables.less
    width: '60px', // 28px is width of small button + 2 * 16px either side.
  },
  collapseButton: {
    ':focus': {
      boxShadow: 'none',
    },
  },
});
